import { NgModule } from '@angular/core';
import { mapToCanActivate, RouterModule, Routes } from '@angular/router';
import { AccountAdminGuard } from './core/guards/account-admin.guard';
import { PartnerAdminGuard } from './core/guards/partner-admin.guard';
import { NavigationErrorComponent } from './shared/components/navigation-error/navigation-error.component';
import { ClaimsGuard } from './core/guards/claims.guard';
import { RBACAuthGuardService } from './core/guards/rbac-auth-guard.service';
import { environment } from '../environments/environment';
import { loadRemoteModule } from '@angular-architects/module-federation-runtime';
import { FeatureFlagsGuard } from './core/guards/feature-flags.guard';
import { PBXSettingsManagementGuard } from './core/guards/pbx-settings-management.guard';
import { EventHistoryGuard } from './core/guards/event-history.guard';

const routes: Routes = [
	{ path: '', canActivate: mapToCanActivate([ClaimsGuard]), children: [
		{ 
			path: '', 
			loadChildren: () => import(`./modules/dashboard/dashboard.module`).then(m => m.DashboardModule),
			canActivate: mapToCanActivate([PartnerAdminGuard])
		},
		{ path: 'support-search', pathMatch: 'full', loadChildren: () => import(`./modules/support-portal/support-portal.module`).then(m => m.SupportPortalModule)},
		{ 
			path: 'feature-flags', loadChildren: () => import(`./modules/feature-flags/feature-flags.module`).then(m => m.FeatureFlagsModule), canActivate: mapToCanActivate([FeatureFlagsGuard]),
		},
		{
			path: 'dashboard/:id', 
			loadChildren: () => import(`./modules/dashboard/dashboard.module`).then(m => m.DashboardModule),
			canActivate: mapToCanActivate([AccountAdminGuard, RBACAuthGuardService]),
			data: { category: 'dashboard', action:'view' }
		},
		{ 
			path: 'accounts/:id/cc/admin', 
			children : [
				{
				path: '',
				loadChildren: () =>
				loadRemoteModule({
					remoteName: "CcMfeWrapper",
					remoteEntry: environment.callCenterSpa.callCentreBaseURL + "/remoteEntry.js",
					exposedModule: "CcAdminMfeWrapperModule",
				}).then(m => m.CcAdminMfeWrapperModule),
				canActivate: mapToCanActivate([AccountAdminGuard]),
				outlet: 'mfe-outlet'
				}
			]
		},
		{ path: 'accounts', pathMatch: 'full', loadChildren: () => import(`./modules/accounts-list/accounts-list.module`).then(m => m.AccountsListModule)},
		{ path: 'accounts/:id', loadChildren: () => import(`./modules/accounts-edit/accounts-edit.module`).then(m => m.AccountsEditModule)},
		
		{ path: 'accounts/:id/users',  loadChildren: () => import(`./modules/users-list/users-list.module`).then(m => m.UsersListModule)},

		{ path: 'accounts/:id/subscriptions', loadChildren: () => import(`./modules/account-subscription-list/account-subscription-list.module`).then(m => m.AccountSubscriptionListModule)},
		{ path: 'accounts/:id/integrations', loadChildren: () => import(`./modules/integrations-applications/integrations-applications.module`).then(m => m.IntegrationsApplicationsModule)},
		{ 
			path: 'accounts/:id/ccdashboard', 
			loadChildren: () => import(`./modules/call-center-iframed/call-center-iframed.module`).then(m => m.CallCenterIframedModule),
			data: { path: 'dashboard', category: 'callCenter', action:'view', origPath: 'ccdashboard' }
		},
		{ 
			path: 'accounts/:id/ccskills',
			loadChildren: () => import(`./modules/call-center-iframed/call-center-iframed.module`).then(m => m.CallCenterIframedModule),
			data: { path: 'skills', category: 'callCenter', action:'view', origPath: 'ccskills' }
		},
		{ 
			path: 'accounts/:id/ccrouting',
			loadChildren: () => import(`./modules/call-center-iframed/call-center-iframed.module`).then(m => m.CallCenterIframedModule),
			data: { path: 'flows', useMic: true, category: 'callCenter', action:'view', origPath: 'ccrouting', unloadTimeoutValue: 100 }
		},
		{ 
			path: 'accounts/:id/ccembeddedchats',
			loadChildren: () => import(`./modules/call-center-iframed/call-center-iframed.module`).then(m => m.CallCenterIframedModule),
			data: { path: 'embedded-chats', category: 'callCenter', action:'view', origPath: 'ccembeddedchats' }
		},
		{ 
			path: 'accounts/:id/cccodes',
			loadChildren: () => import(`./modules/call-center-iframed/call-center-iframed.module`).then(m => m.CallCenterIframedModule),
			data: { path: 'codes', category: 'callCenter', action:'view', origPath: 'cccodes' }
		},
		{ 
			path: 'accounts/:id/cctemplates',
			loadChildren: () => import(`./modules/call-center-iframed/call-center-iframed.module`).then(m => m.CallCenterIframedModule),
			data: { path: 'templates', category: 'callCenter', action:'view', origPath: 'cctemplates' }
		},
		{ 
			path: 'accounts/:id/ccsettings',
			loadChildren: () => import(`./modules/call-center-iframed/call-center-iframed.module`).then(m => m.CallCenterIframedModule),
			data: { path: 'settings', category: 'callCenter', action:'view', origPath: 'ccsettings' }
		},
		{ 
			path: 'accounts/:id/ccintegrations',
			loadChildren: () => import(`./modules/call-center-iframed/call-center-iframed.module`).then(m => m.CallCenterIframedModule),
			data: { path: 'integrations', allowClipboard: true, category: 'callCenter', action:'view', origPath: 'ccintegrations', unloadTimeoutValue: 100 }
		},
		{ 
			path: 'accounts/:id/ccnumbers',
			loadChildren: () => import(`./modules/call-center-iframed/call-center-iframed.module`).then(m => m.CallCenterIframedModule),
			data: { path: 'numbers', category: 'callCenter', action:'view', origPath: 'ccnumbers' }
		},
		{ 
			path: 'accounts/:id/varouting',
			loadChildren: () => import(`./modules/call-center-iframed/call-center-iframed.module`).then(m => m.CallCenterIframedModule),
			data: { path: 'voiceAssistFlows', useMic: true, category: 'callCenter', action:'view', origPath: 'varouting', unloadTimeoutValue: 100 }
		},
		{ 
			path: 'accounts/:id/integrations/social/twilio',
			loadChildren: () => import(`./modules/call-center-iframed/call-center-iframed.module`).then(m => m.CallCenterIframedModule),
			data: { path: 'social/twilio', useMic: true, category: 'callCenter', action:'view', origPath: 'integrations/social/twilio', unloadTimeoutValue: 100 }
		},
		{ 
			path: 'accounts/:id/integrations/social/cmcom',
			loadChildren: () => import(`./modules/call-center-iframed/call-center-iframed.module`).then(m => m.CallCenterIframedModule),
			data: { path: 'social/cmcom', useMic: true, category: 'callCenter', action:'view', origPath: 'integrations/social/cmcom', unloadTimeoutValue: 100 }
		},
		{ 
			path: 'accounts/:id/mivb/systemSettings',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.mivbsettingsSpa.mivbsettingsUrl + "/remoteEntry.js",
					exposedModule: "./MivbsettingsModule",
				}).then(m => m.MivbsettingsModule),
			data: { shell: 'accounts' },
			canActivate: mapToCanActivate([AccountAdminGuard])
		},
		{ 
			path: 'accounts/:id/mivb/groups',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.mivbsettingsSpa.mivbsettingsUrl + "/remoteEntry.js",
					exposedModule: "./GroupManagementModule",
				}).then(m => m.GroupManagementModule),
			data: { shell: 'accounts' },
			canActivate: mapToCanActivate([AccountAdminGuard])
		},
		{
			path: 'accounts/:id/mivb/departments-and-locations',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.mivbsettingsSpa.mivbsettingsUrl + "/remoteEntry.js",
					exposedModule: "./DepartmentsAndLocationsModule",
				}).then(m => m.DepartmentsAndLocationsModule),
			data: { shell: 'accounts' },
			canActivate: mapToCanActivate([AccountAdminGuard])
		},
		{
			path: 'accounts/:id/mivb/emergency-services',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.mivbsettingsSpa.mivbsettingsUrl + "/remoteEntry.js",
					exposedModule: "./PbxSettingsModule",
				}).then(m => m.PbxSettingsModule),
			data: { shell: 'accounts' },
			canActivate: mapToCanActivate([AccountAdminGuard])
		},
		{
			path: 'accounts/:id/mivb/numbers',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.mivbsettingsSpa.mivbsettingsUrl + "/remoteEntry.js",
					exposedModule: "./NumbersModule",
				}).then(m => m.NumbersModule),
			data: { shell: 'accounts' },
			canActivate: mapToCanActivate([AccountAdminGuard])
		},
		{
			path: 'accounts/:id/mivb/tools',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.mivbsettingsSpa.mivbsettingsUrl + "/remoteEntry.js",
					exposedModule: "./ToolsModule",
				}).then(m => m.ToolsModule),
			data: { shell: 'accounts' },
			canActivate: mapToCanActivate([AccountAdminGuard])
		},
		{
			path: 'accounts/:id/mivb/voice-gateway',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.mivbsettingsSpa.mivbsettingsUrl + "/remoteEntry.js",
					exposedModule: "./VoiceGatewayModule",
				}).then(m => m.VoiceGatewayModule),
			data: { shell: 'accounts' },
			canActivate: mapToCanActivate([AccountAdminGuard])
		},
		{ 
			path: 'accounts/:id/mivb/queues',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.mivbsettingsSpa.mivbsettingsUrl + "/remoteEntry.js",
					exposedModule: "./QueueManagementModule",
				}).then(m => m.QueueManagementModule),
			data: { shell: 'accounts' },
			canActivate: mapToCanActivate([AccountAdminGuard])
		},
		{ 
			path: 'accounts/:id/:pbx/systemSettings',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.mivbsettingsSpa.mivbsettingsUrl + "/remoteEntry.js",
					exposedModule: "./PbxSettingsModule",
				}).then(m => m.PbxSettingsModule),
			data: { shell: 'accounts' },
			canActivate: mapToCanActivate([AccountAdminGuard, PBXSettingsManagementGuard])
		},
		{ 
			path: 'roles', 
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.rolesSpa.rolesUrl + "/remoteEntry.js",
					exposedModule: "./RBACModule",
				}).then(m => m.RBACModule),
			data: { category: 'rolesManagement', action:'view' },
			canActivate: mapToCanActivate([PartnerAdminGuard, RBACAuthGuardService])
		},
		{ 
			path: 'accounts/:id/roles',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.rolesSpa.rolesUrl + "/remoteEntry.js",
					exposedModule:  "./RBACModule",
				}).then(m => m.RBACModule),
			data: { shell: 'accounts', category: 'rolesManagement', action:'view' },
			canActivate: mapToCanActivate([AccountAdminGuard, RBACAuthGuardService])
		},
		{ path: 'orders', loadChildren: () => import(`./modules/orders-iframed/orders-iframed.module`).then(m => m.OrdersIframedModule)},
		{
			path: 'developer',
			loadChildren: () =>
				loadRemoteModule({
					remoteName: "cloudlinkDeveloperapis",
					remoteEntry: environment.developerSpa.developerURL + "/remoteEntry.js",
					exposedModule: "DeveloperApisModule",
				}).then(m => m.DeveloperApisModule),
			canActivate: mapToCanActivate([PartnerAdminGuard])
		},
		{
			path: 'accounts/:id/support-logs',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.supportLogsSpa.logsUrl + "/remoteEntry.js",
					exposedModule: "./SupportLogsModule",
				}).then(m => m.SupportLogsModule),
			data: { shell: 'accounts', category: 'supportLogs', action: 'view' },
			canActivate: mapToCanActivate([AccountAdminGuard, RBACAuthGuardService])
		},
		{
			path: 'accounts/:id/event-history',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.supportLogsSpa.logsUrl + "/remoteEntry.js",
					exposedModule: "./EventHistoryModule",
				}).then(m => m.EventHistoryModule),
			data: { shell: 'accounts'},
			canActivate: [EventHistoryGuard] //AccountAdminGuard guard will be run from within EventHistoryGuard
		},
		{
			path: 'accounts/:id/zappa-support',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.supportLogsSpa.logsUrl + "/remoteEntry.js",
					exposedModule: "./ZappaSupportModule",
				}).then(m => m.ZappaSupportModule),
			canActivate: mapToCanActivate([AccountAdminGuard]),
		},
		{ path: 'accounts/:id/gateway', loadChildren: () => import(`./modules/gateway-portal-iframed/gateway-portal-iframed.module`).then(m => m.GatewayPortalIframedModule)},
		{ path: 'accounts/:id/:redirect/gateway', loadChildren: () => import(`./modules/gateway-portal-iframed/gateway-portal-iframed.module`).then(m => m.GatewayPortalIframedModule)},
		{ path: 'accounts/:id/gatewaysupport', loadChildren: () => import(`./modules/gateway-support-iframed/gateway-support-iframed.module`).then(m => m.GatewaySupportIframedModule)},
		{ path: 'managed-accounts', pathMatch: 'full', loadChildren: () => import(`./modules/managed-accounts/managed-accounts.module`).then(m => m.ManagedAccountsModule)},
		{ path: 'account-management', loadChildren: () => import(`./modules/account-management/account-management.module`).then(m => m.AccountManagementModule) },
		{
			path: 'user-templates',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.templateManagementSpa.templateManagementUrl + "/remoteEntry.js",
					exposedModule: "./UserTemplatesModule",
				}).then(m => m.UserTemplatesModule),
			canActivate: mapToCanActivate([PartnerAdminGuard])
		},
		{
			path: 'accounts/:id/user-templates',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.templateManagementSpa.templateManagementUrl + "/remoteEntry.js",
					exposedModule: "./UserTemplatesModule",
				}).then(m => m.UserTemplatesModule),
			canActivate: mapToCanActivate([AccountAdminGuard])
		},
		{
			path: 'accounts/:id/management-profiles',
			loadChildren: () =>
				loadRemoteModule({
					type: 'module',
					remoteEntry: environment.rolesSpa.rolesUrl + '/remoteEntry.js',
					exposedModule: './ManagementProfilesModule',
				}).then(m => m.ManagementProfilesModule),
			data: { shell: 'accounts', category: 'managementProfiles', action:'view' },
			canActivate: mapToCanActivate([AccountAdminGuard, RBACAuthGuardService])
		},
    {
      path: 'platforms',
      loadChildren: () =>
          loadRemoteModule({
            type: 'module',
            remoteEntry: environment.systemInventorySpa.systemInventoryUrl + '/remoteEntry.js',
            exposedModule: './SystemInventoryModule',
          }).then(m => m.SystemInventoryModule),
      data: { path: 'platforms', action: 'list' },
      canActivate: mapToCanActivate([ PartnerAdminGuard ])
    },
    { 
      path: 'accounts/:id/platforms',
      loadChildren: () =>
          loadRemoteModule({
            type: 'module',
            remoteEntry: environment.systemInventorySpa.systemInventoryUrl + '/remoteEntry.js',
            exposedModule: './SystemInventoryModule',
          }).then(m => m.SystemInventoryModule),
      data: { shell: 'accounts', path: 'platforms', action: 'list' },
      canActivate: mapToCanActivate([ AccountAdminGuard ])
    },
    { 
      path: 'applications',
      loadChildren: () =>
          loadRemoteModule({
            type: 'module',
            remoteEntry: environment.systemInventorySpa.systemInventoryUrl + '/remoteEntry.js',
            exposedModule: './SystemInventoryModule',
          }).then(m => m.SystemInventoryModule),
      data: { path: 'applications', action: 'list' },
      canActivate: mapToCanActivate([ PartnerAdminGuard ])
    },
    { 
      path: 'accounts/:id/applications',
      loadChildren: () =>
          loadRemoteModule({
            type: 'module',
            remoteEntry: environment.systemInventorySpa.systemInventoryUrl + '/remoteEntry.js',
            exposedModule: './SystemInventoryModule',
          }).then(m => m.SystemInventoryModule),
      data: { shell: 'accounts', path: 'applications', action: 'list' },
      canActivate: mapToCanActivate([ AccountAdminGuard ])
    }  
	]},	
	{ path: 'navigationError', component: NavigationErrorComponent },
	{ path: '**', redirectTo: '' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: false /*, enableTracing: true */ })],
  exports: [ RouterModule ]
})
export class AppRoutingModule { }

